<script setup lang="ts">
import { tpgIconCheck, tpgIconError } from '@tpg/grammar-web-icons';
import { toTypedSchema } from '@vee-validate/yup';
import { useField } from 'vee-validate';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

import type { GrammarTextFieldExpose } from '@nora-monorepo/grammar-ui';
import { GrammarTextField } from '@nora-monorepo/grammar-ui';
import { CONVERSATION_TITLE_MAX_LENGTH } from '@nora-monorepo/shared';

import { createConversationTitleSchema } from '#ui-shared/validation/conversation-title.schema';

interface ConversationTitleInputProps {
  /**
   * Represents the vee-validate field path for the conversation title.
   */
  name: string;
  label?: string;
  placeholder?: string;
  readonly?: boolean;
}

const props = defineProps<ConversationTitleInputProps>();
const conversationTitleRef = ref<GrammarTextFieldExpose | null>(null);
const { t } = useI18n();

const { value, errorMessage, meta } = useField<string>(
  () => props.name,
  toTypedSchema(createConversationTitleSchema(t)),
  { validateOnMount: true },
);

/**
 * Lets parent modals focus the input when the modal is shown.
 */
defineExpose({
  focus: () => conversationTitleRef.value?.focus(),
});
</script>

<template>
  <GrammarTextField
    ref="conversationTitleRef"
    :value="value"
    :readonly="props.readonly"
    :label="label ?? t('conversationDialog.titleLabel')"
    :placeholder="placeholder ?? t('conversationDialog.titlePlaceholder')"
    :trailing-icon-template="meta.valid ? tpgIconCheck : tpgIconError"
    :form-associated="true"
    :counter="CONVERSATION_TITLE_MAX_LENGTH"
    :type="meta.valid ? 'default' : 'error'"
    :message="errorMessage"
    @change="value = $event"
    @input="value = $event"
  />
</template>
